import React from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import GlLoader from '../../../share/GlLoader'
import RecapInputTag from './inputs/recap/RecapInputTag'
import {
  CompareGroupType,
  StudyObjectType,
} from './configs/StudiesObjectsCompareGroupTypesConfig'
import {
  CompareGroupValueType,
  StudyObjectValueType,
} from '../../../../store/interfaces/analytics-details/new-readership-profile-state.interface'
import { SetColorConfig } from '../../../../core/config/SetColorConfig'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectAnalyticsCompareGroupFiltersActions,
  selectAnalyticsCompareGroupFiltersPayload,
  selectAnalyticsStudyObjectFiltersActions,
  selectAnalyticsStudyObjectFiltersPayload,
  selectNewAnalyticsReadershipProfileStateCompareGroupBooks,
  selectNewAnalyticsReadershipProfileStateCompareGroupType,
  selectNewAnalyticsReadershipProfileStateCompareGroupValue,
  selectNewAnalyticsReadershipProfileStateStudyObjectBooks,
  selectNewAnalyticsReadershipProfileStateStudyObjectType,
  selectNewAnalyticsReadershipProfileStateStudyObjectValue,
} from '../../../../store/getters/analytics-details/new-analytics-readership-profile.getters'
import GlVDivider from '../../../share/GlVDivider'
import DetailsSelection from './inputs/DetailsSelection'
import {
  newReadershipProfileStateGotToStartStep,
  setAnalyticsCompareGroupFiltersPayload,
  setAnalyticsStudyObjectFiltersPayload,
} from '../../../../store/reducers/analytics-details/new-analytics-readership-profile-state.reducer'
import { newAnalyticsPrivateReadershipProfileStatePostPreviewForReadershipProfile } from '../../../../store/actions/analytics-details/new-analytics-readership-profile.actions'
import { ReadershipProfilePreviewUsersInputInterface } from '../../../../core/interface/analytics/readership-profile-preview-users-input.interface'

const RecapInputStyle = styled.div`
  overflow: hidden;
  color: #252525;
  font-variant-numeric: lining-nums proportional-nums;
  text-overflow: ellipsis;
  font-family: Fira Sans, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const ButtonStyle = styled.button`
  width: 280px;
  height: 40px;
  text-align: center;
  font-family: Fira Sans, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;

  background-color: ${({ isPrimary }: { isPrimary: boolean }) =>
    isPrimary ? '#252525' : '#2525251a'};
  color: ${({ isPrimary }: { isPrimary: boolean }) =>
    isPrimary ? 'white' : '#3B3B3B'};

  :disabled {
    background-color: #e9e9e9;
    color: #929292;
  }
`

const AnalyticDetailsNewRequestDetailsStep = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const studyObjectType = useAppSelector(
    selectNewAnalyticsReadershipProfileStateStudyObjectType,
  )
  const studyObjectValue = useAppSelector(
    selectNewAnalyticsReadershipProfileStateStudyObjectValue,
  )
  const studyObjectBooks = useAppSelector(
    selectNewAnalyticsReadershipProfileStateStudyObjectBooks,
  )
  const filtersActionsStudyObject = useAppSelector(
    selectAnalyticsStudyObjectFiltersActions,
  )

  const filtersPayloadStudyObject = useAppSelector(
    selectAnalyticsStudyObjectFiltersPayload,
  )

  const compareGroupType = useAppSelector(
    selectNewAnalyticsReadershipProfileStateCompareGroupType,
  )
  const compareGroupValue = useAppSelector(
    selectNewAnalyticsReadershipProfileStateCompareGroupValue,
  )
  const compareGroupBooks = useAppSelector(
    selectNewAnalyticsReadershipProfileStateCompareGroupBooks,
  )
  const filtersActionsCompareGroup = useAppSelector(
    selectAnalyticsCompareGroupFiltersActions,
  )

  const filtersPayloadCompareObject = useAppSelector(
    selectAnalyticsCompareGroupFiltersPayload,
  )

  const handleOnChangeFiltersPayloadStudyObject = (
    filters: ReadershipProfilePreviewUsersInputInterface,
  ) => {
    dispatch(setAnalyticsStudyObjectFiltersPayload(filters))
  }

  const handleOnChangeFiltersPayloadCompareGroup = (
    filters: ReadershipProfilePreviewUsersInputInterface,
  ) => {
    dispatch(setAnalyticsCompareGroupFiltersPayload(filters))
  }

  const handleClickEditSearch = () => {
    dispatch(newReadershipProfileStateGotToStartStep())
  }

  const handleClickValidateSearch = () => {
    void dispatch(
      newAnalyticsPrivateReadershipProfileStatePostPreviewForReadershipProfile(),
    )
  }

  return (
    <div className="flex-auto flex flex-col px-8 py-4 gap-4">
      <RecapInputStyle
        className="flex-none flex flex-col bg-white rounded justify-between"
        style={{
          boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.05)',
        }}
      >
        {(!studyObjectValue && (
          <div className="flex flex-row items-center justify-center ">
            <GlLoader width={32} height={32} />
          </div>
        )) || (
          <div
            className="flex-1 flex flex-row flex-wrap gap-2 justify-center items-center"
            style={{
              padding: '8px 8px 8px 32px',
            }}
          >
            <span>{t('iStudyTheReadersOf')}</span>
            <RecapInputTag<StudyObjectType, StudyObjectValueType>
              type={studyObjectType}
              value={studyObjectValue}
              color={SetColorConfig[0].color1}
              booksSelected={studyObjectBooks}
            />
            <span>{t('inComparisonWithThoseOf')}</span>
            <RecapInputTag<CompareGroupType, CompareGroupValueType>
              type={compareGroupType}
              value={compareGroupValue}
              color={SetColorConfig[0].color2}
              booksSelected={compareGroupBooks}
            />
          </div>
        )}
      </RecapInputStyle>
      <div
        className="flex flex-col flex-auto bg-white rounded p-8 pb-6 gap-8"
        style={{
          boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.05)',
          justifyContent: 'space-between',
        }}
      >
        <div className="flex-auto flex flex-row items-start justify-center gap-8">
          <DetailsSelection
            title={t('detailsReadershipProfile.studyObjectSettings')}
            type={studyObjectType}
            value={studyObjectValue}
            selectionType="studyObjectValue"
            filtersActions={filtersActionsStudyObject}
            userFilters={filtersPayloadStudyObject}
            onChangeFiltersPayload={handleOnChangeFiltersPayloadStudyObject}
          />
          <GlVDivider height="100%" />
          <DetailsSelection
            title={t('detailsReadershipProfile.comparisonGroupSettings')}
            type={compareGroupType}
            value={compareGroupValue}
            selectionType="compareGroupValue"
            filtersActions={filtersActionsCompareGroup}
            userFilters={filtersPayloadCompareObject}
            onChangeFiltersPayload={handleOnChangeFiltersPayloadCompareGroup}
          />
        </div>
        <div className="flex flex-row gap-4 items-center justify-center">
          <ButtonStyle
            isPrimary={false}
            type="button"
            className="rounded"
            onClick={handleClickEditSearch}
          >
            {t('EditTheSearch')}
          </ButtonStyle>
          <ButtonStyle
            isPrimary
            type="button"
            className="rounded"
            onClick={handleClickValidateSearch}
          >
            {t('validateTheSearch')}
          </ButtonStyle>
        </div>
      </div>
    </div>
  )
}

export default AnalyticDetailsNewRequestDetailsStep
